/**
 * A tab left open across a redeploy still points at chunk files the new build
 * no longer ships; the lazy route's import then fails with a 404 and React
 * Router shows «Unexpected Application Error!». Vite fires `vite:preloadError`
 * for that: reload the page to pick up the new build.
 *
 * Loop guard: the time of the last reload is kept in `sessionStorage`, and the
 * page reloads only when that was more than {@link PRELOAD_RELOAD_WINDOW_MS}
 * ago. A chunk that is really broken cannot make the tab reload in a loop —
 * the next error inside the window falls through to the error page — and a
 * second redeploy in the same tab still recovers. The storage is read lazily,
 * inside the guard: with site data blocked the `sessionStorage` getter itself
 * throws, and that must never break boot.
 */
export const PRELOAD_RELOAD_FLAG = 'admin:preload-reloaded-at';
export const PRELOAD_RELOAD_WINDOW_MS = 30_000;

/** Reloads unless this tab did so within the window; `true` when it reloaded. */
export const reloadOnceAfterPreloadError = (
  getStorage: () => Storage,
  reload: () => void,
  now: number = Date.now(),
): boolean => {
  try {
    const storage = getStorage();
    const last = Number(storage.getItem(PRELOAD_RELOAD_FLAG));
    if (Number.isFinite(last) && last > 0 && now - last <= PRELOAD_RELOAD_WINDOW_MS) return false;
    storage.setItem(PRELOAD_RELOAD_FLAG, String(now));
  } catch {
    // No storage — no way to stop a loop, so do not reload at all.
    return false;
  }
  reload();
  return true;
};

export const installStaleChunkReload = (
  target: Pick<EventTarget, 'addEventListener'>,
  getStorage: () => Storage,
  reload: () => void,
): void => {
  target.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceAfterPreloadError(getStorage, reload)) event.preventDefault();
  });
};
